<template>
  <div>
    <ModalVue
      :modal-active="modalActive"
      max-width="!max-w-[1000px]"
      has-footer-border
      has-header-border
      @close="handleClose"
    >
      <template #header>
        <h2 class="text-lg font-bold text-gray-900 text-left w-full">
          Importar configurações de excepcionalidade
        </h2>
      </template>

      <Tabs v-model="activeTab" :tabs="tabItems">
        <template #templates>
          <ConfigTemplateTab
            ref="configTemplateTabRef"
            v-model="selectedTemplateId"
            :saved-template="savedTemplate"
            :templates="approvalFlowStore.templates"
            :is-deleting="isDeletingTemplate"
            @delete="openDeleteConfirm"
          />
        </template>
        <template #scales>
          <UnitsAndScalesTab
            v-model="selectedScaleId"
            :grouped-data="approvalFlowStore.groupedTemplates"
          />
        </template>
      </Tabs>

      <template #footer>
        <div class="flex gap-4 w-full">
          <div class="flex-1">
            <OutlineButton text="Cancelar" width="100%" @click="handleClose" />
          </div>
          <div class="flex-1">
            <FilledButton
              text="Importar"
              width="100%"
              :disabled="!hasSelection"
              @click="handleSave"
              class="bg-primary30"
            />
          </div>
        </div>
      </template>
    </ModalVue>

    <ConfirmDeleteRoleModal
      v-model="showDeleteConfirm"
      title="Excluir modelo"
      description="Tem certeza que deseja excluir esse modelo de validação de excepcionalidade? Essa ação não poderá ser revertida"
      :loading="isDeletingTemplate"
      @confirm="handleConfirmDelete"
    />

    <ModalVue
      :modal-active="showSuccessModal"
      max-width="!max-w-[480px]"
      :show-close-button="false"
      has-header-border
      @close="closeSuccessModal"
    >
      <template #header>
        <h2 class="text-lg font-bold text-gray-900 text-left w-full">
          Importar configurações de excepcionalidade
        </h2>
      </template>

      <div class="flex flex-col items-center gap-4 py-6">
        <SuccessIcon width="64" height="64" color="#2E7D32" />
        <p class="text-base text-textPrimary text-center">
          Configurações de excepcionalidade importadas<br />
          com sucesso para essa escala
        </p>
      </div>

      <template #footer>
        <FilledButton text="Fechar" width="100%" class="bg-primary30" @click="closeSuccessModal" />
      </template>
    </ModalVue>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import Tabs from '@/components/designSystem/Tabs/Tabs.vue'
import type { TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import ConfirmDeleteRoleModal from '@/components/ManageScale/Schedule/modals/ConfirmDeleteRoleModal.vue'
import SuccessIcon from '@/assets/icons/success.vue'
import ConfigTemplateTab from './components/ConfigTemplateTab.vue'
import UnitsAndScalesTab from './components/UnitsAndScalesTab.vue'
import { useApprovalFlowStore } from '@/pinia-store/useApprovalFlowStore'
import { useErrorToast } from '@/composables/useErrorToast'

const approvalFlowStore = useApprovalFlowStore()
const { notifyError } = useErrorToast()

const props = defineProps<{
  modalActive: boolean
  savedTemplate?: { id: number; name: string } | null
  scheduleId?: number
  companyId?: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', payload: { source: 'template' | 'scale'; id: number }): void
}>()

const tabItems: TabItem[] = [
  { label: 'Modelos de Configuração', value: 'templates' },
  { label: 'Unidades e Escalas', value: 'scales' }
]

const configTemplateTabRef = ref<InstanceType<typeof ConfigTemplateTab> | null>(null)
const activeTab = ref('templates')
const selectedTemplateId = ref<number | null>(null)
const selectedScaleId = ref<number | null>(null)
const showDeleteConfirm = ref(false)
const templateToDeleteId = ref<number | null>(null)
const isDeletingTemplate = ref(false)
const showSuccessModal = ref(false)

const openDeleteConfirm = (id: number) => {
  templateToDeleteId.value = id
  showDeleteConfirm.value = true
}

const handleConfirmDelete = async () => {
  if (templateToDeleteId.value !== null && props.companyId) {
    isDeletingTemplate.value = true
    try {
      await approvalFlowStore.deleteTemplate(templateToDeleteId.value, props.companyId)
      configTemplateTabRef.value?.removeTemplate()
    } catch (error) {
      // A store NÃO exibe nada (só relança): antes a exclusão falhava em silêncio.
      notifyError(error, 'Não foi possível excluir o modelo de configuração.')
    } finally {
      isDeletingTemplate.value = false
    }
  }
  showDeleteConfirm.value = false
  templateToDeleteId.value = null
}

watch(
  () => props.modalActive,
  (active) => {
    if (active) {
      if (props.scheduleId) {
        // A action relança desde o M2: sem o catch, a falha virava unhandled
        // rejection e a aba aparecia vazia, como se não houvesse modelos.
        approvalFlowStore.fetchTemplatesBySchedule(props.scheduleId).catch((error) => {
          notifyError(error, 'Não foi possível carregar os modelos de configuração.')
        })
      }
      if (props.savedTemplate) {
        activeTab.value = 'templates'
        selectedTemplateId.value = props.savedTemplate.id
      }
    }
  }
)

watch(activeTab, (tab) => {
  if (tab === 'scales' && props.companyId) {
    approvalFlowStore.fetchGroupedTemplates(props.companyId).catch((error) => {
      notifyError(error, 'Não foi possível carregar as unidades e escalas com modelos.')
    })
  }
})

const hasSelection = computed(() => {
  return activeTab.value === 'templates'
    ? selectedTemplateId.value !== null
    : selectedScaleId.value !== null
})

const resetState = () => {
  activeTab.value = 'templates'
  selectedTemplateId.value = null
  selectedScaleId.value = null
}

const closeSuccessModal = () => {
  showSuccessModal.value = false
}

const handleClose = () => {
  resetState()
  emit('close')
}

const handleSave = () => {
  if (!hasSelection.value) return

  if (activeTab.value === 'templates' && selectedTemplateId.value !== null) {
    emit('save', { source: 'template', id: selectedTemplateId.value })
  } else if (activeTab.value === 'scales' && selectedScaleId.value !== null) {
    emit('save', { source: 'template', id: selectedScaleId.value })
  }

  resetState()
}

const onImportSuccess = () => {
  showSuccessModal.value = true
}

defineExpose({ onImportSuccess })
</script>
